import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';

import { Coord } from '@everyboard/games';
import { CoordSet } from '@everyboard/games';
import { Orthogonal } from '@everyboard/games';
import { Player } from '@everyboard/games';
import { RulesFailure } from '@everyboard/games';
import { Debug } from '@everyboard/games';
import { SiamFailure } from '@everyboard/games/siam';
import { SiamHeuristic } from '@everyboard/games/siam';
import { SiamMove } from '@everyboard/games/siam';
import { SiamMoveGenerator } from '@everyboard/games/siam';
import { SiamPiece } from '@everyboard/games/siam';
import { SiamConfig, SiamLegalityInformation, SiamRules } from '@everyboard/games/siam';
import { SiamState } from '@everyboard/games/siam';
import { MGPOptional, MGPValidation, Utils } from '@everyboard/lib';

import { ViewBox } from '../../components/game-components/GameComponentUtils';
import { ClickHandler } from '../../components/game-components/game-component/ClickHandler';
import { RectangularGameComponent } from '../../components/game-components/rectangular-game-component/RectangularGameComponent';

import { SiamOrientationArrowComponent } from './siam-orientation-arrow.component';

export type SiamIndicatorArrow = {
    source: MGPOptional<{ coord: Coord; piece: SiamPiece }>;
    target: Coord;
    direction: Orthogonal;
    move: SiamMove;
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-siam',
    templateUrl: './siam.component.html',
    styleUrls: ['../../components/game-components/game-component/game-component.scss'],
    imports: [NgClass, SiamOrientationArrowComponent],
})
@Debug.log
export class SiamComponent extends RectangularGameComponent<SiamRules,
                                                            SiamMove,
                                                            SiamState,
                                                            SiamPiece,
                                                            SiamConfig,
                                                            SiamLegalityInformation>
{
    public SiamState: typeof SiamState = SiamState;
    public lastMove: MGPOptional<SiamMove> = MGPOptional.empty();
    public movedPieces: Coord[] = [];
    public selectedPiece: MGPOptional<Coord> = MGPOptional.empty();
    public selectedLanding: MGPOptional<Coord> = MGPOptional.empty();
    public orientations: SiamMove[] = [];
    public clickableCoords: CoordSet = new CoordSet();
    public indicatorArrows: SiamIndicatorArrow[] = [];

    private insertingPiece: boolean = false;

    public constructor() {
        super('Siam');
        this.aiConfig = {
            minimax: [{
                id: 'Distance',
                name: $localize`Distance`,
                heuristic: (): SiamHeuristic => new SiamHeuristic(),
                moveGenerator: (): SiamMoveGenerator => new SiamMoveGenerator(),
            }],
            mcts: [{
                id: 'default',
                name: $localize`MCTS`,
                moveGenerator: (): SiamMoveGenerator => new SiamMoveGenerator(),
            }],
        };
        this.encoder = SiamMove.encoder;
    }

    protected override computeViewBox(): ViewBox {
        return super.computeViewBox()
            .expand(0, 0, this.SPACE_SIZE, this.SPACE_SIZE);
    }

    public override async updateBoard(_triggerAnimation: boolean): Promise<void> {
        const state: SiamState = this.state();
        this.board = state.board;
    }

    protected override async showLastMove(move: SiamMove): Promise<void> {
        this.lastMove = MGPOptional.of(move);
        const previousGameState: SiamState = this.getPreviousState();
        const config: SiamConfig = this.config();
        this.movedPieces = this.rules.isLegal(this.lastMove.get(), previousGameState, config).get().moved;
    }

    public override hideLastMove(): void {
        this.lastMove = MGPOptional.empty();
        this.movedPieces = [];
    }

    public override cancelMoveAttempt(): void {
        this.insertingPiece = false;
        this.selectedPiece = MGPOptional.empty();
        this.selectedLanding = MGPOptional.empty();
        this.orientations = [];
        this.clickableCoords = new CoordSet();
        this.indicatorArrows = [];
    }


    @ClickHandler((player: Player) => `#remaining-pieces-${ player.toString() }`)
    public async selectPieceForInsertion(player: Player): Promise<MGPValidation> {
        if (player === this.getCurrentOpponent()) {
            return this.cancelMove(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_OPPONENT());
        }
        if (this.insertingPiece) {
            // We were already inserting, we deselect the piece
            return this.cancelMove();
        }
        this.cancelMoveAttempt();
        const config: SiamConfig = this.config();
        for (const move of SiamRules.get().getInsertions(this.state(), config)) {
            const target: Coord = move.coord.getNext(move.direction.get());
            // For every pushing insertion, we draw an arrow in case it will push a piece
            if (this.board[target.y][target.x] !== SiamPiece.EMPTY) {
                const arrow: SiamIndicatorArrow = {
                    source: MGPOptional.empty(),
                    target,
                    direction: move.direction.get(),
                    move,
                };
                this.indicatorArrows.push(arrow);
            }
            this.clickableCoords = this.clickableCoords.addElement(target);
        }
        this.insertingPiece = true;
        return MGPValidation.SUCCESS;
    }

    @ClickHandler((move: SiamMove) => `#orientation-${ move.landingOrientation.toString() }`)
    public async selectOrientation(move: SiamMove): Promise<MGPValidation> {
        // The player has clicked on an orientation arrow, we know the move directly
        return this.chooseMove(move);
    }

    @ClickHandler((x: number, y: number) => `#square-${ x }-${ y }`)
    public async clickSquare(x: number, y: number): Promise<MGPValidation> {
        // doSquareClick is called somewhere else where no click handling check need to be done
        return this.doSquareClick(x, y);
    }

    private async doSquareClick(x: number, y: number): Promise<MGPValidation> {
        const clickedCoord: Coord = new Coord(x, y);
        if (this.insertingPiece) {
            return this.insertPiece(clickedCoord);
        } else {
            // Clicking a square to select a piece or end a non-insertion move
            if (this.selectedLanding.isPresent()) {
                // Player clicked somewhere on the board instead of an orientation arrow, cancel the move
                return this.cancelMove();
            } else if (this.selectedPiece.isPresent()) {
                // Select the landing
                this.selectedLanding = MGPOptional.of(clickedCoord);
                const moves: SiamMove[] =
                    SiamRules.get().getMovesBetween(this.state(),
                                                    this.state().getPieceAt(this.selectedPiece.get()),
                                                    this.selectedPiece.get(),
                                                    clickedCoord);
                if (moves.length === 0) {
                    return this.changeMoveDestinationClick(clickedCoord);
                }
                return this.performMoveOrShowOrientationArrows(moves);
            } else {
                Utils.assert(this.state().isOnBoard(clickedCoord), 'SiamComponent: user clicked outside of board when it should not be possible');
                const clickedPiece: SiamPiece = this.board[y][x];
                if (clickedPiece.getOwner().isNone()) {
                    return this.cancelMove(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_EMPTY());
                } else if (clickedPiece.getOwner() === this.getCurrentPlayer()) {
                    // Select the piece
                    return this.selectPiece(clickedCoord, clickedPiece);
                } else {
                    return this.cancelMove(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_OPPONENT());
                }
            }
        }
    }

    private async insertPiece(clickedCoord: Coord): Promise<MGPValidation> {
        if (this.selectedLanding.isPresent()) {
            // The landing is already selected, we cancel the move to avoid any confusion
            await this.cancelMove(SiamFailure.MUST_SELECT_ORIENTATION());
            return MGPValidation.SUCCESS;
        }
        // Inserting a new piece, the player just clicked on the landing
        this.selectedLanding = MGPOptional.of(clickedCoord);
        const config: SiamConfig = this.config();
        const insertions: SiamMove[] =
            SiamRules.get().getInsertionsAt(this.state(), clickedCoord.x, clickedCoord.y, config);
        if (insertions.length === 0) {
            return this.changeMoveDestinationClick(clickedCoord);
        }
        return this.performMoveOrShowOrientationArrows(insertions);
    }

    private async changeMoveDestinationClick(clickedCoord: Coord): Promise<MGPValidation> {
        // The player clicked somewhere where there are no possible move, cancel the move
        this.cancelMoveAttempt();
        const piece: SiamPiece = this.state().getPieceAt(clickedCoord);
        if (piece.getOwner() === this.getCurrentPlayer()) {
            // The click was made on another piece of the player, likely to select it
            return this.doSquareClick(clickedCoord.x, clickedCoord.y);
        } else {
            // The click was made on an invalid destination
            return this.cancelMove(SiamFailure.MUST_SELECT_VALID_DESTINATION());
        }
    }

    private async performMoveOrShowOrientationArrows(availableMoves: SiamMove[]): Promise<MGPValidation> {
        Utils.assert(availableMoves.length > 0, 'SiamComponent.performMoveOrShowOrientationArrows expects at least one move');
        if (availableMoves.length === 1) {
            // There's only one possible move, so perform it
            return this.chooseMove(availableMoves[0]);
        } else {
            // Since there's more than a single move, the player will have to select the orientation
            this.clickableCoords = new CoordSet();
            this.indicatorArrows = [];
            this.orientations = availableMoves;
            return MGPValidation.SUCCESS;
        }
    }

    private selectPiece(clickedCoord: Coord, clickedPiece: SiamPiece): MGPValidation {
        this.cancelMoveAttempt();
        const state: SiamState = this.state();
        this.selectedPiece = MGPOptional.of(clickedCoord);
        const moves: SiamMove[] =
            SiamRules.get().getMovesFrom(state, clickedPiece, clickedCoord.x, clickedCoord.y);
        for (const move of moves) {
            if (move.direction.isPresent()) {
                const target: Coord = move.coord.getNext(move.direction.get());
                this.clickableCoords = this.clickableCoords.addElement(target);
                if (state.hasInequalPieceAt(target, SiamPiece.EMPTY)) {
                    const arrow: SiamIndicatorArrow = {
                        source: MGPOptional.of({
                            coord: clickedCoord,
                            piece: clickedPiece,
                        }),
                        target,
                        direction: move.direction.get(),
                        move,
                    };
                    this.indicatorArrows.push(arrow);
                }
            } else {
                this.clickableCoords = this.clickableCoords.addElement(move.coord);
            }
        }
        return MGPValidation.SUCCESS;
    }

    @ClickHandler((arrow: SiamIndicatorArrow) => `#indicator-${ arrow.target.x }-${ arrow.target.y }-${ arrow.move.landingOrientation }`)
    public async clickArrow(arrow: SiamIndicatorArrow): Promise<MGPValidation> {
        // The user clicked on an arrow directly instead of a square,
        // we can perform the move without asking for the orientation
        return this.chooseMove(arrow.move);
    }

    public isMountain(piece: SiamPiece): boolean {
        return piece === SiamPiece.MOUNTAIN;
    }

    public getSiamArrowTransform(x: number, y: number, direction: Orthogonal): string {
        const orientation: number = direction.toInt() - 2;
        const rotation: string = this.getRotation(orientation);
        const translation: string = this.getSVGTranslation(x * this.SPACE_SIZE, y * this.SPACE_SIZE);
        return [translation, rotation].join(' ');
    }

    private getRotation(orientation: number): string {
        return `rotate(${orientation * 90} ${this.SPACE_SIZE / 2} ${this.SPACE_SIZE / 2})`;
    }

    public getPieceRotation(x: number, y: number): string {
        const piece: SiamPiece = this.board[y][x];
        const orientation: number = piece.getDirection().toInt() - 2;
        return this.getRotation(orientation);
    }

    public getPieceTransform(x: number, y: number): string {
        const piece: SiamPiece = this.board[y][x];
        return this.getSiamArrowTransform(x, y, piece.getDirection());
    }

    public getIndicatorTransform(arrow: SiamIndicatorArrow): string {
        const startingAt: Coord = arrow.target.getPrevious(arrow.direction, 0.5);
        return this.getSiamArrowTransform(startingAt.x, startingAt.y, arrow.direction);
    }

    public getRemainingPieceTransform(piece: number, player: Player): string {
        const config: SiamConfig = this.config();
        const cx: number = config.width / 2;
        const offset: number = 1 / 2;
        const pieceOnBoard: number = this.state().countPlayersPawn().get(player);
        const nbPieceToDraw: number = config.numberOfPiece - pieceOnBoard;
        const width: number = 1 + ((nbPieceToDraw - 1) * offset);
        let x: number;
        let y: number;
        let orientation: Orthogonal;
        if (player === this.getPointOfView()) {
            x = cx - (width / 2) + (piece * offset);
            y = config.height;
            orientation = Orthogonal.LEFT;
        } else {
            // Top pieces are stacked right-to-left for better visuals
            x = cx + (width / 2) - 1 - (piece * offset);
            y = -1;
            orientation = Orthogonal.RIGHT;
        }
        return this.getSiamArrowTransform(x, y, orientation);
    }

    public getPieceClasses(x: number, y: number, c: SiamPiece): string[] {
        const coord: Coord = new Coord(x, y);
        const classes: string[] = [this.getPlayerClass(c.getOwner())];
        if (this.selectedPiece.equalsValue(coord)) {
            classes.push('selected-stroke');
        }
        return classes;
    }

    public getSquareClasses(x: number, y: number): string[] {
        const coord: Coord = new Coord(x, y);

        if (this.movedPieces.some((c: Coord) => c.equals(coord))) {
            return ['moved-fill'];
        }
        return [];
    }

    public getRemainingPieceClasses(player: Player, pieceIndex: number): string[] {
        const classes: string[] = [this.getPlayerClass(player)];
        if (this.insertingPiece && this.getCurrentPlayer() === player && pieceIndex === this.playerPieces(player)-1) {
            classes.push('selected-stroke');
        }
        return classes;
    }

    public playerPieces(player: Player): number {
        const maxPiece: number = this.config().numberOfPiece;
        const pieceOnBoard: number = this.state().countPlayersPawn().get(player);
        return maxPiece - pieceOnBoard;
    }

}
